import { z } from "zod";
import { Id } from "../common/ids.js";
import { Timestamp } from "../common/time.js";
import { defineRoute } from "../http/route.js";
import { CustomPayload } from "./payloads.js";
/** The most a text may hold (ADR 0029, owner 2026-10-30), counted in UTF-16 units as JavaScript counts them. */
export const MESSAGE_TEXT_MAX = 5000;
export const MessageKind = z.enum(["text", "card", "notice", "file", "recall", "removal"]).meta({
    id: "MessageKind",
    description: "What an entry is: a person's text, the platform's card, notice, or file, or a recall or removal of an earlier entry",
});
export const MessageState = z.enum(["visible", "recalled", "removed"]).meta({
    id: "MessageState",
    description: "An entry's final state; a recalled or removed entry shows neither its text nor its payload",
});
/**
 * An entry in a conversation's log (ADR 0029). Its number places it, never a clock: numbers start at 1 and have no
 * gaps, so a client that holds up to `n` asks for what follows `n`.
 */
export const MessageEntry = z
    .object({
    id: Id,
    conversationId: Id,
    seq: z.number().int().positive().describe("The entry's place in its conversation, from 1, without gaps"),
    clientId: Id.nullable().describe("The id its sender's client gave it, or one its cause decided: a pending send meets its entry by it (card 148)"),
    kind: MessageKind,
    sender: z
        .object({ userId: Id, enterpriseId: Id })
        .nullable()
        .describe("Who wrote or posted it, in which enterprise; null for the platform's own notices"),
    body: z.string().nullable().describe("A visible text's words, as plain text; null otherwise"),
    payload: CustomPayload.nullable().describe("A visible card's, notice's, or file's envelope, versioned with `v`; null otherwise"),
    targetSeq: z
        .number()
        .int()
        .positive()
        .nullable()
        .describe("For a recall or a removal: the entry it acts on"),
    state: MessageState,
    sentAt: Timestamp,
})
    .meta({ id: "MessageEntry" });
export const sendMessage = defineRoute({
    surface: "tenant",
    auth: "user",
    permission: "conversation:create",
    id: "messaging.messages.send",
    method: "POST",
    path: "/v1/conversations/{conversationId}/messages",
    marks: ["tenant"],
    summary: "Send a text into a conversation from the desktop app. The client makes one id per message, so a retry with it returns the same entry and never sends twice",
    prd: ["IM-1"],
    request: {
        params: z.object({ conversationId: Id }),
        body: z.object({
            clientId: z
                .uuid()
                .describe("Made by the client once per message, and sent again with every retry of it"),
            kind: z.literal("text"),
            body: z
                .string()
                .min(1)
                .max(MESSAGE_TEXT_MAX)
                .refine((text) => text.trim().length > 0, "Write something"),
        }),
    },
    responses: {
        201: { description: "The entry: new, or the one this client id already made", schema: MessageEntry },
    },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "NOT_FOUND",
        "CONVERSATION_CLOSED",
        "RATE_LIMITED",
    ],
});
export const postFile = defineRoute({
    surface: "tenant",
    auth: "user",
    permission: "conversation:create",
    id: "messaging.messages.post_file",
    method: "POST",
    path: "/v1/conversations/{conversationId}/files",
    marks: ["tenant"],
    summary: "Post a clean chat file the caller uploaded for this conversation. Its name, type, and size come from its record; posting the same file again returns the same entry",
    prd: ["IM-6"],
    request: {
        params: z.object({ conversationId: Id }),
        body: z.object({ fileId: Id }),
    },
    responses: {
        201: { description: "The file's entry: new, or the one already posted", schema: MessageEntry },
    },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "NOT_FOUND",
        "FILE_NOT_CLEAN",
        "FILE_REMOVED",
        "CONVERSATION_CLOSED",
        "RATE_LIMITED",
    ],
});
/** How long after sending a person may recall their own text or file (card 131; owner, 2026-10-29). */
export const RECALL_SECONDS = 120;
export const recallMessage = defineRoute({
    surface: "tenant",
    auth: "user",
    permission: "conversation:create",
    id: "messaging.messages.recall",
    method: "POST",
    path: "/v1/conversations/{conversationId}/messages/{messageId}/recall",
    marks: ["tenant"],
    summary: "Recall one's own text or file within 2 minutes of sending. Every member then sees it recalled, and the platform keeps its text for support's reading. Recalling it again answers the same recall; cards and notices record trade steps and are never recalled",
    prd: ["IM-1"],
    request: { params: z.object({ conversationId: Id, messageId: Id }) },
    responses: {
        201: { description: "The recall's entry, naming the entry it recalls", schema: MessageEntry },
    },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "NOT_FOUND",
        "INVALID_TRANSITION",
        "WINDOW_CLOSED",
        "RATE_LIMITED",
    ],
});
/** An entry's number in its conversation, as a query parameter. */
const SeqParam = z.coerce.number().int().nonnegative();
export const MessagePage = z
    .object({
    items: z.array(MessageEntry).describe("In ascending order of number"),
    firstKeptSeq: z
        .number()
        .int()
        .positive()
        .describe("Where history begins; older entries were removed by retention (card 101)"),
    lastSeq: z.number().int().nonnegative().describe("The number of the log's latest entry"),
})
    .meta({ id: "MessagePage" });
export const listMessages = defineRoute({
    surface: "tenant",
    auth: "user",
    permission: "conversation:create",
    id: "messaging.messages.list",
    method: "GET",
    path: "/v1/conversations/{conversationId}/messages",
    marks: ["tenant"],
    summary: "A page of a conversation's log: after a number, to catch up; before one, to page back; or the newest page. Recalls and removals are entries; a recalled or removed entry carries no text",
    prd: ["IM-1"],
    request: {
        params: z.object({ conversationId: Id }),
        query: z
            .object({
            after: SeqParam.optional().describe("Entries after this number, to catch up; 0 for the beginning"),
            before: SeqParam.optional().describe("Entries before this number, to page back"),
            limit: z.coerce.number().int().min(1).max(200).default(50),
        })
            .refine((query) => query.after === undefined || query.before === undefined, {
            message: "Ask for entries after a number or before one, not both",
            path: ["before"],
        }),
    },
    responses: { 200: { description: "The page", schema: MessagePage } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const ReadState = z
    .object({
    readSeq: z.number().int().nonnegative(),
    unreadCount: z.number().int().nonnegative(),
})
    .meta({ id: "ReadState" });
export const markRead = defineRoute({
    surface: "tenant",
    auth: "user",
    permission: "conversation:create",
    id: "messaging.messages.read",
    method: "POST",
    path: "/v1/conversations/{conversationId}/read",
    marks: ["tenant"],
    summary: "Say how far the caller has read. It only moves forward; a number past the log's latest entry counts as the latest",
    prd: ["IM-1"],
    request: {
        params: z.object({ conversationId: Id }),
        body: z.object({ seq: z.number().int().nonnegative() }),
    },
    responses: { 200: { description: "Where the caller now stands", schema: ReadState } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const getReceipts = defineRoute({
    surface: "tenant",
    auth: "user",
    permission: "conversation:create",
    id: "messaging.messages.receipts",
    method: "GET",
    path: "/v1/conversations/{conversationId}/receipts",
    marks: ["tenant"],
    summary: 'How far each active member has read, for "Read" and "Read by n"',
    prd: ["IM-1"],
    request: { params: z.object({ conversationId: Id }) },
    responses: {
        200: {
            description: "Each active member's read number",
            schema: z.object({
                members: z.array(z.object({ userId: Id, enterpriseId: Id, readSeq: z.number().int().nonnegative() })),
            }),
        },
    },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const messageRoutes = [
    sendMessage,
    postFile,
    recallMessage,
    listMessages,
    markRead,
    getReceipts,
];
//# sourceMappingURL=message.routes.js.map